/* v05_a24-dawn-refactor/assets/a24-collection-filters-ps-filter-range.js */
/**
 * Shared PS-range behavior for collection and search filters.
 * Search submission routing is handled centrally by a24-search-routing.js.
 */
(function () {
  class A24PsFilterRange {
    // Speichert Referenzen auf Host-Element, Formular und alle PS-Filterblöcke.
    constructor(owner) {
      this.owner = owner;
      this.root = owner;
      this.form = owner.form;
      this.blocks = Array.from(this.root.querySelectorAll('[data-ps-range-block]'));
    }

    // Initialisiert jeden PS-Block inkl. Events, Presets und aktivem Chip.
    init() {
      this.blocks.forEach((block) => {
        this.prefillInputs(block);
        this.storeInitialState(block);
        this.bindInputs(block);
        this.bindPresets(block);
        this.syncPresetButtons(block);
        this.renderChip(block);
      });
    }

    // Platzhalter für API-Kompatibilität; Cleanup macht das Host-Element.
    destroy() {
      // Event listeners are registered through the owning custom element.
    }

    // Wendet die PS-Range auf alle Blöcke an und bricht bei erstem Fehler ab.
    applyAll(context = {}) {
      return this.blocks.every((block) => this.applyRange(block, context));
    }

    // Überträgt den gewählten Min/Max-Bereich auf die nativen Shopify-Optionen.
    applyRange(block, context = {}) {
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const options = this.getRangeOptions(block);
      const selectableOptions = this.getSelectableRangeOptions(block);
      if (!minInput || !maxInput || !options.length) return true;

      const bounds = this.resolveRangeBounds(block, context);
      if (bounds.invalid) {
        this.showError(block, bounds.message || 'Der Bis-Wert muss größer oder gleich dem Von-Wert sein.');
        this.setNativeValidation(bounds.focusInput || maxInput || minInput, bounds.message || 'Der Bis-Wert muss größer oder gleich dem Von-Wert sein.', context);
        return false;
      }

      if (!bounds.hasSelection) {
        options.forEach(({ input }) => { input.checked = false; });
        this.renderChip(block);
        return true;
      }

      this.clearError(block);
      options.forEach(({ input, value }) => {
        const isSelectable = selectableOptions.some((option) => option.input === input);
        input.checked = isSelectable && value >= bounds.min && value <= bounds.max;
      });
      this.renderChip(block);
      return true;
    }

    // Liest alle PS-Optionen inkl. numerischem Wert und Facet-Count aus dem DOM.
    getRangeOptions(block) {
      return Array.from(block.querySelectorAll('[data-ps-range-option]'))
        .map((input) => ({
          input,
          value: this.parseNumber(input.dataset.psRangeNumber || input.value),
          count: this.parseInteger(input.dataset.psRangeCount)
        }))
        .filter(({ value }) => Number.isFinite(value))
        .sort((a, b) => a.value - b.value);
    }

      // Liefert nur aktuell filterbare PS-Optionen (Count > 0 oder bereits aktiv).
    getSelectableRangeOptions(block) {
      const options = this.getRangeOptions(block);
      if (!options.length) return [];

      const hasFacetCounts = options.some(({ count }) => Number.isFinite(count));
      if (!hasFacetCounts) return options;

      // Shopify liefert die vollständige Werteliste; filterbar sind aber nur
      // Werte mit Count > 0 (aktive Werte bleiben trotzdem erhalten).
      return options.filter(({ input, count }) => input.checked || count > 0);
    }

    // Ermittelt den effektiven Bereich aus Eingaben, Presets und verfügbaren Optionen.
    resolveRangeBounds(block, context = {}) {
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const options = this.getSelectableRangeOptions(block);
      const availableMin = options[0]?.value;
      const availableMax = options[options.length - 1]?.value;
      const typedMin = this.parseNumber(minInput?.value);
      const typedMax = this.parseNumber(maxInput?.value);
      const hasMin = Number.isFinite(typedMin);
      const hasMax = Number.isFinite(typedMax);
      const presetRequest = this.getActivePresetRequest(block);
      const allowsOpenPresetBound = Boolean(presetRequest?.openBound);

      if (!hasMin && !hasMax && !presetRequest) {
        return { hasSelection: false, invalid: false, min: null, max: null };
      }

      const hasOnlyOneValue = hasMin !== hasMax;
      if (this.owner.config?.psInputRequirement === 'both' && hasOnlyOneValue && !allowsOpenPresetBound) {
        const message = 'Bitte „Von PS“ und „Bis PS“ ausfüllen.';
        return {
          hasSelection: false,
          invalid: true,
          min: null,
          max: null,
          message,
          focusInput: hasMin ? maxInput : minInput
        };
      }

      const min = this.resolveRequestedMinimum(options, typedMin, presetRequest);
      const max = this.resolveRequestedMaximum(options, typedMax, presetRequest);
      const invalid = Number.isFinite(min) && Number.isFinite(max) && min > max;
      return {
        hasSelection: Number.isFinite(min) && Number.isFinite(max),
        invalid,
        message: invalid ? 'Der Bis-Wert muss größer oder gleich dem Von-Wert sein.' : '',
        focusInput: invalid ? maxInput : null,
        min,
        max
      };
    }

    // Liest die aktuell in data-Attributen gespeicherte Preset-Anfrage aus.
    getActivePresetRequest(block) {
      if (!block?.dataset?.psRangePresetKey) return null;

      return {
        key: block.dataset.psRangePresetKey || '',
        label: block.dataset.psRangePresetLabel || '',
        openBound: block.dataset.psRangePresetOpen || '',
        min: this.parseNumber(block.dataset.psRangePresetMin),
        max: this.parseNumber(block.dataset.psRangePresetMax),
        minExclusive: this.parseNumber(block.dataset.psRangePresetMinExclusive)
      };
    }

    // Bestimmt die angeforderte Untergrenze für die Bereichsauswahl.
    resolveRequestedMinimum(options, typedMin, presetRequest = null) {
      const availableMin = options[0]?.value;
      if (!options.length) return Number.NaN;

      if (Number.isFinite(presetRequest?.minExclusive)) {
        const firstAboveExclusiveMinimum = options.find(({ value }) => value > presetRequest.minExclusive);
        return firstAboveExclusiveMinimum?.value ?? Number.NaN;
      }

      if (Number.isFinite(presetRequest?.min)) return presetRequest.min;
      if (Number.isFinite(typedMin)) return typedMin;
      return availableMin;
    }

    // Bestimmt die angeforderte Obergrenze für die Bereichsauswahl.
    resolveRequestedMaximum(options, typedMax, presetRequest = null) {
      const availableMax = options[options.length - 1]?.value;
      if (!options.length) return Number.NaN;

      if (Number.isFinite(presetRequest?.max)) return presetRequest.max;
      if (Number.isFinite(typedMax)) return typedMax;
      return availableMax;
    }

    // Gibt die ausgewählten nativen Shopify-Werte innerhalb des Bereichs zurück.
    getSelectedRangeValues(block) {
      const bounds = this.resolveRangeBounds(block);
      if (!bounds.hasSelection || bounds.invalid) return [];
      return this.getRangeOptions(block)
        .filter(({ value }) => value >= bounds.min && value <= bounds.max)
        .map(({ input }) => String(input.value || '').trim())
        .filter(Boolean);
    }

      // Leitet auf /collections/all um, wenn PS-Auswahl auf /search geändert wurde.
    redirectChangedSearchPsToCollection() {
      if (!this.form || !this.hasChangedPsSelectionFromUrl()) return false;

      /*
       * Die Suchseite rendert Shopify-Filterwerte nur aus dem aktuellen
       * Suchergebnis. Nach „Suzuki 15 PS“ fehlen dort z. B. oft 20, 30 oder
       * 60 PS, obwohl sie in der Zielkollektion verfügbar sind. Deshalb wird
       * die aktuelle Filterliste der Zielkollektion zuerst geladen und der
       * Bereich danach aus diesen frischen Optionen aufgebaut.
       */
      this.navigateChangedSearchPsToCollection();
      return true;
    }

    // Baut Ziel-URL und frische PS-Werte asynchron vor der Navigation auf.
    async navigateChangedSearchPsToCollection() {
      if (window.A24Theme?.shouldProtectEditorClicks?.()) return;

      const target = this.buildCollectionRedirectTarget();
      if (!target) return;

      window.A24SearchRouting?.showLoading?.('Filter werden geladen…');

      try {
        const freshOptions = await this.fetchCollectionPsOptions(target);
        this.appendPsRangeValues(target, freshOptions);
      } catch (error) {
        /*
         * Fallback: Bei einem Netzwerkproblem bleiben die aktuell gerenderten
         * Shopify-Werte nutzbar. Die Navigation wird dadurch nicht blockiert.
         */
        console.warn('A24: PS-Filterwerte konnten nicht frisch geladen werden.', error);
        this.appendPsRangeValues(target, this.getRenderedPsOptions());
      }

      window.location.assign(target.toString());
    }

    // Erstellt die Ziel-URL für die Kollektion inkl. aller Nicht-PS-Filter.
    buildCollectionRedirectTarget() {
      const target = new URL(window.A24SearchRouting?.allProductsUrl || '/collections/all', window.location.origin);
      const ignored = new Set([
        'q',
        'type',
        'page',
        'a24_ps_search_mode',
        'a24_brand_search_mode'
      ]);
      const psParamNames = [...new Set(this.blocks.flatMap((block) => this.getParamNames(block)))];

      new FormData(this.form).forEach((value, name) => {
        if (!ignored.has(name) && !psParamNames.includes(name) && value !== '' && value !== null) {
          target.searchParams.append(name, value);
        }
      });

      psParamNames.forEach((name) => target.searchParams.delete(name));
      return target;
    }

    // Lädt die PS-Optionen aus der Zielkollektion per AJAX.
    async fetchCollectionPsOptions(target) {
      const source = new URL(target.toString());
      source.searchParams.delete('page');

      const response = await fetch(source.toString(), {
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
      const options = Array.from(documentFragment.querySelectorAll('[data-ps-range-option]'))
        .map((input) => ({
          value: this.parseNumber(input.getAttribute('data-ps-range-number') || input.getAttribute('value')),
          raw: String(input.getAttribute('value') || '').trim()
        }))
        .filter(({ value, raw }) => Number.isFinite(value) && raw);

      return options.length ? options : this.getRenderedPsOptions();
    }

    // Fallback: nimmt die momentan im aktuellen DOM gerenderten PS-Optionen.
    getRenderedPsOptions() {
      return this.blocks.flatMap((block) => this.getRangeOptions(block)
        .map(({ input, value }) => ({ value, raw: String(input.value || '').trim() }))
      ).filter(({ value, raw }) => Number.isFinite(value) && raw);
    }

    // Schreibt alle PS-Werte im gewünschten Bereich in die Ziel-URL.
    appendPsRangeValues(target, availableOptions) {
      const uniqueOptions = Array.from(new Map(
        availableOptions
          .filter(({ value, raw }) => Number.isFinite(value) && raw)
          .map(({ value, raw }) => [String(value), { value, raw }])
      ).values()).sort((a, b) => a.value - b.value);

      this.blocks.forEach((block) => {
        const psParamName = this.getParamNames(block)[0];
        if (!psParamName || !uniqueOptions.length) return;

        const minInput = block.querySelector('[data-ps-range-min]');
        const maxInput = block.querySelector('[data-ps-range-max]');
        const typedMin = this.parseNumber(minInput?.value);
        const typedMax = this.parseNumber(maxInput?.value);
        const presetRequest = this.getActivePresetRequest(block);
        const min = this.resolveRequestedMinimum(uniqueOptions, typedMin, presetRequest);
        const max = this.resolveRequestedMaximum(uniqueOptions, typedMax, presetRequest);

        if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return;

        uniqueOptions
          .filter(({ value }) => value >= min && value <= max)
          .forEach(({ raw }) => target.searchParams.append(psParamName, raw));
      });
    }

    // Backward-compatible alias for older callers.
    redirectChangedExactSearchToCollection() {
      return this.redirectChangedSearchPsToCollection();
    }

    // Prüft, ob die sichtbare PS-Auswahl von der URL-Auswahl abweicht.
    hasChangedPsSelectionFromUrl() {
      if (!window.location.pathname.includes('/search')) return false;

      /*
       * The visible number fields are the customer-facing source of truth.
       * Their initial values are stored on first render because Shopify can
       * normalize filter values differently in the URL (for example 15 vs.
       * 15,0). Comparing only the generated checkbox values can therefore
       * miss a real PS change and submit the search form again.
       */
      if (this.blocks.some((block) => this.hasChangedInputState(block))) return true;

      const currentUrl = new URL(window.location.href);
      const formData = new FormData(this.form);
      const parameterNames = [...new Set(this.blocks.flatMap((block) => this.getParamNames(block)))];

      return parameterNames.some((name) => {
        const before = currentUrl.searchParams.getAll(name).map(String).sort();
        const after = formData.getAll(name).map(String).sort();
        return before.length !== after.length || before.some((value, index) => value !== after[index]);
      });
    }

    // Speichert initiale Eingabewerte für spätere Change-Erkennung.
    storeInitialState(block) {
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      if (!minInput || !maxInput) return;

      block.dataset.psRangeInitialMin = this.normalizeComparableNumber(minInput.value);
      block.dataset.psRangeInitialMax = this.normalizeComparableNumber(maxInput.value);
    }

    // Erkennt Änderungen gegenüber den initial gespeicherten PS-Eingaben.
    hasChangedInputState(block) {
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      if (!minInput || !maxInput) return false;

      return block.dataset.psRangeInitialMin !== this.normalizeComparableNumber(minInput.value) ||
        block.dataset.psRangeInitialMax !== this.normalizeComparableNumber(maxInput.value);
    }

    // Normalisiert Zahlenwerte als stabile String-Repräsentation zum Vergleichen.
    normalizeComparableNumber(value) {
      const number = this.parseNumber(value);
      return Number.isFinite(number) ? String(number) : '';
    }

    // Füllt leere Min/Max-Eingaben aus bereits aktiven PS-Werten vor.
    prefillInputs(block) {
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const values = this.getActiveValues(block);
      if (!minInput || !maxInput || !values.length) return;
      if (!String(minInput.value).trim()) minInput.value = this.formatNumber(Math.min(...values));
      if (!String(maxInput.value).trim()) maxInput.value = this.formatNumber(Math.max(...values));
    }

    // Bindet Fokus-, Input-, Change- und Blur-Handler für Min/Max-Felder.
    bindInputs(block) {
      ['[data-ps-range-min]', '[data-ps-range-max]'].forEach((selector) => {
        const input = block.querySelector(selector);
        this.owner.addListener(input, 'focus', () => {
          block.dataset.psRangeFocusedAt = String(Date.now());
        });
        this.owner.addListener(input, 'input', () => {
          this.clearPresetState(block);
          this.clearError(block);
          this.renderChip(block, true);
          this.syncPresetButtons(block);
          this.owner.queueAutoApply({ source: 'ps', block, input });
        });
        this.owner.addListener(input, 'change', () => {
          this.clearPresetState(block);
          this.clearError(block);
          this.renderChip(block, true);
          this.syncPresetButtons(block);
          this.owner.queueAutoApply({ source: 'ps', block, input });
        });
        this.owner.addListener(input, 'blur', () => {
          if (this.hasSingleValue(block)) {
            this.owner.queueAutoApply({ source: 'ps', block, input });
          }
        });
      });
    }

    // Verknüpft Preset-Buttons mit der Preset-Anwendung.
    bindPresets(block) {
      block.querySelectorAll('[data-ps-range-preset]').forEach((button) => {
        this.owner.addListener(button, 'click', (event) => {
          event.preventDefault();
          this.applyPreset(block, button);
        });
      });
    }

    // Übernimmt ein Preset in die Eingaben und triggert Auto-Apply.
    applyPreset(block, button) {
      if (!block || !button || button.disabled) return;

      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const presetRange = this.resolvePresetRange(block, button);
      if (!minInput || !maxInput || !presetRange) return;

      minInput.value = Number.isFinite(presetRange.inputMin) ? this.formatNumber(presetRange.inputMin) : '';
      maxInput.value = Number.isFinite(presetRange.inputMax) ? this.formatNumber(presetRange.inputMax) : '';
      this.storePresetState(block, button, presetRange);
      this.clearError(block);
      this.applyRange(block, { source: 'ps-preset' });
      this.syncPresetButtons(block);
      this.owner.queueAutoApply({ source: 'ps', block, input: button });
    }

    // Rechnet Preset-Definitionen in einen konkreten, verfügbaren Bereich um.
    resolvePresetRange(block, button) {
      const options = this.getSelectableRangeOptions(block);
      if (!options.length) return null;

      const availableMin = options[0].value;
      const availableMax = options[options.length - 1].value;
      const configuredMin = this.parseNumber(button.dataset.psRangePresetMin);
      const configuredMax = this.parseNumber(button.dataset.psRangePresetMax);
      const configuredMinExclusive = this.parseNumber(button.dataset.psRangePresetMinExclusive);
      const openBound = button.dataset.psRangePresetOpen || '';

      let min = Number.isFinite(configuredMin) ? configuredMin : availableMin;
      let max = Number.isFinite(configuredMax) ? configuredMax : availableMax;
      let inputMin = min;
      let inputMax = max;

      if (Number.isFinite(configuredMinExclusive)) {
        const firstAboveExclusiveMinimum = options.find(({ value }) => value > configuredMinExclusive);
        if (!firstAboveExclusiveMinimum) return null;
        min = firstAboveExclusiveMinimum.value;
        inputMin = Math.floor(configuredMinExclusive) + 1;
      }

      if (openBound === 'min') {
        min = availableMin;
        inputMin = Number.NaN;
      }

      if (openBound === 'max') {
        max = availableMax;
        inputMax = Number.NaN;
      }

      const matchingOptions = options.filter(({ value }) => value >= min && value <= max);
      if (!matchingOptions.length) return null;

      return {
        min,
        max,
        inputMin,
        inputMax,
        openBound,
        minExclusive: configuredMinExclusive
      };
    }

    // Persistiert den aktiven Preset-Status im Block-Dataset.
    storePresetState(block, button, presetRange) {
      block.dataset.psRangePresetKey = button.dataset.psRangePresetKey || '';
      block.dataset.psRangePresetLabel = button.dataset.psRangePresetLabel || String(button.textContent || '').replace(/\s+/g, ' ').trim();
      block.dataset.psRangePresetOpen = presetRange.openBound || '';
      block.dataset.psRangePresetMin = Number.isFinite(presetRange.min) ? String(presetRange.min) : '';
      block.dataset.psRangePresetMax = Number.isFinite(presetRange.max) ? String(presetRange.max) : '';
      block.dataset.psRangePresetMinExclusive = Number.isFinite(presetRange.minExclusive) ? String(presetRange.minExclusive) : '';
    }

    // Entfernt den Preset-Status und synchronisiert die Preset-Buttons neu.
    clearPresetState(block) {
      if (!block) return;
      delete block.dataset.psRangePresetKey;
      delete block.dataset.psRangePresetLabel;
      delete block.dataset.psRangePresetOpen;
      delete block.dataset.psRangePresetMin;
      delete block.dataset.psRangePresetMax;
      delete block.dataset.psRangePresetMinExclusive;
      this.syncPresetButtons(block);
    }

    // Aktiviert/deaktiviert Presets abhängig von der aktuellen PS-Verfügbarkeit.
    syncPresetButtons(block) {
      const canFilterByPs = this.canFilterByPs(block);
      const presetsRoot = block.querySelector('[data-ps-range-presets]');
      if (presetsRoot) {
        presetsRoot.classList.toggle('is-disabled', !canFilterByPs);
        presetsRoot.setAttribute('aria-disabled', !canFilterByPs ? 'true' : 'false');
      }

      if (!canFilterByPs) {
        delete block.dataset.psRangePresetKey;
        delete block.dataset.psRangePresetLabel;
        delete block.dataset.psRangePresetOpen;
        delete block.dataset.psRangePresetMin;
        delete block.dataset.psRangePresetMax;
        delete block.dataset.psRangePresetMinExclusive;
      }

      block.querySelectorAll('[data-ps-range-preset]').forEach((button) => {
        const presetRange = this.resolvePresetRange(block, button);
        const isDisabled = !canFilterByPs || !presetRange;
        const isActive = !isDisabled && block.dataset.psRangePresetKey === (button.dataset.psRangePresetKey || '');
        button.disabled = isDisabled;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    // Gibt an, ob aktuell ein sinnvoller PS-Bereich filterbar ist.
    canFilterByPs(block) {
      const options = this.getSelectableRangeOptions(block);
      return options.length >= 2;
    }

    // Prüft, ob nur eines der beiden Eingabefelder befüllt ist.
    hasSingleValue(block) {
      if (!block) return false;
      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const hasMin = Number.isFinite(this.parseNumber(minInput?.value));
      const hasMax = Number.isFinite(this.parseNumber(maxInput?.value));
      return hasMin !== hasMax;
    }

    // Smart-Modus: wartet auf zweites Feld, wenn der Nutzer dort noch tippt.
    shouldWaitForSecondField(block) {
      if (!block || this.owner.config?.psInputRequirement !== 'smart' || !this.hasSingleValue(block)) return false;

      const minInput = block.querySelector('[data-ps-range-min]');
      const maxInput = block.querySelector('[data-ps-range-max]');
      const hasMin = Number.isFinite(this.parseNumber(minInput?.value));
      const partnerInput = hasMin ? maxInput : minInput;

      return partnerInput === document.activeElement && !String(partnerInput?.value || '').trim();
    }

    // Liest alle aktuell aktivierten PS-Werte als Zahlen aus.
    getActiveValues(block) {
      return Array.from(block.querySelectorAll('[data-ps-range-option]:checked'))
        .map((input) => this.parseNumber(input.dataset.psRangeNumber || input.value))
        .filter(Number.isFinite);
    }

    // Liefert die eindeutigen Parameternamen der nativen PS-Filteroptionen.
    getParamNames(block) {
      return [...new Set(Array.from(block.querySelectorAll('[data-ps-range-option][name]'))
        .map((input) => input.name).filter(Boolean))];
    }

    // Rendert/aktualisiert den aktiven PS-Chip in der Filter-Chip-Leiste.
    renderChip(block, useTypedValues = false) {
      const chipsRoot = this.root.querySelector('.active-filter-chips');
      if (!chipsRoot) return;
      chipsRoot.querySelector('[data-ps-range-active-chip]')?.remove();
      this.hideNativeChips(chipsRoot, block);

      if (!this.canFilterByPs(block)) return;

      const min = this.parseNumber(block.querySelector('[data-ps-range-min]')?.value);
      const max = this.parseNumber(block.querySelector('[data-ps-range-max]')?.value);
      const values = useTypedValues ? [] : this.getActiveValues(block);
      const displayMin = Number.isFinite(min) ? min : (values.length ? Math.min(...values) : null);
      const displayMax = Number.isFinite(max) ? max : (values.length ? Math.max(...values) : null);
      if (displayMin === null && displayMax === null) return;

      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'active-filter-chip';
      chip.dataset.psRangeActiveChip = '';
      chip.dataset.filterKey = block.dataset.filterKey || this.getParamNames(block)[0] || '';
      chip.setAttribute('aria-label', this.label(block) + ' entfernen');
      chip.textContent = this.chipLabel(block, displayMin, displayMax);
      this.owner.addListener(chip, 'click', () => this.reset(block));
      chipsRoot.appendChild(chip);
      this.owner.sortActiveFilterChips();
    }

    // Blendet native Shopify-PS-Chips aus, damit nur der Custom-Chip sichtbar ist.
    hideNativeChips(chipsRoot, block) {
      const label = this.label(block).toLowerCase();
      chipsRoot.querySelectorAll('a.active-filter-chip').forEach((chip) => {
        const text = String(chip.textContent || '').toLowerCase();
        const isPs = text.includes('leistung') || /(^|\s)ps\s*:/i.test(text) || (label && text.includes(label));
        chip.hidden = isPs;
        chip.toggleAttribute('aria-hidden', isPs);
      });
    }

    // Setzt PS-Eingaben und Selektion zurück und löst Filter-Neuanwendung aus.
    reset(block) {
      block.querySelector('[data-ps-range-min]').value = '';
      block.querySelector('[data-ps-range-max]').value = '';
      block.querySelectorAll('[data-ps-range-option]').forEach((input) => { input.checked = false; });
      this.clearPresetState(block);
      this.clearError(block);
      this.renderChip(block);
      this.syncPresetButtons(block);
      if (typeof this.owner.applyFilters === 'function') {
        this.owner.applyFilters({ manual: true, source: 'ps-reset' });
        return;
      }

      this.form?.requestSubmit();
    }

    // Liefert das sichtbare Label des PS-Filterblocks.
    label(block) {
      const title = block.querySelector('.filter-block__title-text') || block.querySelector('.filter-block__title');
      return String(title?.textContent || '').replace(/\s+/g, ' ').trim() || 'Leistung (PS)';
    }

    // Erzeugt den Text für den aktiven PS-Chip.
    chipLabel(block, min, max) {
      const label = this.label(block);
      const presetLabel = String(block.dataset.psRangePresetLabel || '').trim();
      if (presetLabel) return `${label}: ${presetLabel}`;
      if (min !== null && max !== null) return `${label}: ${this.formatNumber(min)}–${this.formatNumber(max)}`;
      if (min !== null) return `${label} ab ${this.formatNumber(min)}`;
      return `${label} bis ${this.formatNumber(max)}`;
    }

    // Zeigt Validierungs-/Hinweismeldungen unterhalb der PS-Eingaben an.
    showError(block, message, tone = 'error') {
      if (!block) return;
      let element = block.querySelector('[data-ps-range-error]');
      if (!element) {
        element = document.createElement('p');
        element.dataset.psRangeError = '';
        element.setAttribute('role', 'alert');
        (block.querySelector('.filter-range-inputs') || block).insertAdjacentElement('afterend', element);
      }

      element.className = tone === 'info' ? 'filter-range-inputs__error is-info' : 'filter-range-inputs__error';
      element.textContent = message;
    }

    // Setzt browsernative Validierung am fokussierten Eingabefeld.
    setNativeValidation(input, message, context = {}) {
      if (!input) return;
      input.setCustomValidity(message);
      input.title = message;

      if (context.manual) {
        input.focus();
        input.reportValidity?.();
      }
    }

    // Entfernt PS-Fehlerausgabe und native Validierungszustände.
    clearError(block) {
      block.querySelector('[data-ps-range-error]')?.remove();
      block.querySelectorAll('[data-ps-range-min], [data-ps-range-max]').forEach((input) => {
        input.setCustomValidity('');
        input.removeAttribute('title');
      });
    }

    // Parst Eingaben tolerant zu einer Dezimalzahl.
    parseNumber(value) {
      const normalized = String(value ?? '').trim().replace(/\s+/g, '').replace(',', '.');
      if (!normalized) return Number.NaN;
      const number = Number.parseFloat(normalized);
      return Number.isFinite(number) ? number : Number.NaN;
    }

    // Parst Eingaben als Ganzzahl (z. B. Facet-Counts).
    parseInteger(value) {
      const normalized = String(value ?? '').trim();
      if (!normalized) return Number.NaN;
      const number = Number.parseInt(normalized, 10);
      return Number.isFinite(number) ? number : Number.NaN;
    }

    // Formatiert Zahlen für Eingabefelder mit Komma-Dezimaltrennzeichen.
    formatNumber(value) {
      const number = Number(value);
      return Number.isInteger(number) ? String(number) : String(number).replace('.', ',');
    }
  }

  window.A24PsFilterRange = A24PsFilterRange;
}());
